// Landing — thin wrapper around the eager Hero shell + lazy BelowFold.
// PERF-LCP-1 split the eager hero out so React can mount the LCP <img>
// in the first commit.
// PERF-LCP-4 additionally splits LandingHeader + PromoBar into an async
// chunk because LandingHeader pulls in useAuth / useRole / useMyEvents,
// which transitively drag the supabase client (+ auth-js, realtime-js,
// storage-js, postgrest-js, phoenix — ~170 kB raw) into whatever chunk
// loads them. Keeping the header eager would mean supabase ships in
// the entry chunk and the landing's initial JS payload stays over the
// 150 kB budget. A logo-only sync skeleton avoids any header-shaped
// CLS while the real header hydrates.
//
// `heroCtaRef` is lifted here because BelowFold's <StickyMobileCta>
// needs to observe the hero CTA sentinel that lives inside <Hero>.
import { lazy, Suspense, useRef } from "react";
import Hero from "./landing/Hero";
import confettiCamLogo from "@/assets/conftticam-logo.webp";
import { isCampaignActive } from "@/lib/campaign";

const LandingHeader = lazy(() =>
  import("@/components/landing/LandingHeader").then((m) => ({ default: m.LandingHeader })),
);
const PromoBar = lazy(() =>
  import("@/components/landing/PromoBar").then((m) => ({ default: m.PromoBar })),
);
const BelowFold = lazy(() => import("./landing/BelowFold"));

// Logo-only static header so the page paints with branding immediately.
// Heights are hardcoded (h-[53px] mobile, sm:h-[73px]) to exactly match
// the hydrated LandingHeader: py-2/sm:py-4 + img h-9/sm:h-10 + 1px border.
// This eliminates the hero's downward shift when the real header mounts.
function HeaderSkeleton() {
  return (
    <header className="sticky top-0 z-20 h-[53px] border-b border-black/[0.04] bg-white/85 backdrop-blur-xl sm:h-[73px]">
      <div className="mx-auto flex h-full max-w-[1120px] items-center justify-between gap-4 px-4 py-2 sm:px-8 sm:py-4">
        <span className="flex min-w-0 items-center" aria-hidden="true">
          <img
            src={confettiCamLogo}
            alt=""
            className="h-9 w-auto sm:h-10"
            width={1240}
            height={280}
          />
        </span>
        {/* Reserve action-group height so the live header doesn't shift layout. */}
        <span className="h-[34px]" aria-hidden="true" />
      </div>
    </header>
  );
}

// Reserve exact PromoBar height so the hero doesn't jump when the lazy
// promo chunk mounts. Only render the reservation when the campaign is
// active — `isCampaignActive` is a sync date check, so we know at first
// paint whether PromoBar will be shown. Heights match PromoBar's
// min-h-[48px] mobile / min-h-[52px] sm + 1px border-b.
function PromoBarSkeleton() {
  if (!isCampaignActive()) return null;
  return (
    <div
      aria-hidden="true"
      className="h-[49px] w-full border-b border-accent-deep/10 bg-accent-soft sm:h-[53px]"
    />
  );
}

export default function Landing() {
  const heroCtaRef = useRef<HTMLDivElement>(null);

  return (
    <div className="relative min-h-screen bg-white pb-20 md:pb-0">
      {/* skip-to-content link lives in App.tsx (global). */}
      <Suspense fallback={<HeaderSkeleton />}>
        <LandingHeader />
      </Suspense>
      <Suspense fallback={<PromoBarSkeleton />}>
        <PromoBar />
      </Suspense>
      <main id="main">
        <Hero heroCtaRef={heroCtaRef} />
        <Suspense fallback={null}>
          <BelowFold heroCtaRef={heroCtaRef} />
        </Suspense>
      </main>
    </div>
  );
}
